import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import MedicalReportsList from './components/MedicalReportsList';
import Login from './components/Login';
import PrivateRoute from './components/PrivateRoute';
import { AuthProvider } from './hooks/useAuth';
import { SocketProvider } from './contexts/SocketContext';
import { MessagesUIProvider } from './contexts/MessagesUIContext';
import Header from './components/Header';
import UserManagement from './components/UserManagement';
import MfaSettings from './components/MfaSettings';
import MedicalExamExport from './components/MedicalExamExport';
import RolePermissions from './components/RolePermissions';
import LandingPage from './components/LandingPage';
import AdminLanding from './components/AdminLanding';
import Account from './components/Account';
import SalesTransactions from './components/SalesTransactions';
import SalesForm from './components/SalesForm';
import SalesQueue from './components/SalesQueue';
import BulkInvoice from './components/BulkInvoice';
import DoctorSalesSummary from './components/DoctorSalesSummary';
import DoctorSalesDetails from './components/DoctorSalesDetails';
import DoctorCommissionConfig from './components/DoctorCommissionConfig';
import CustomerAgingSummary from './components/CustomerAgingSummary';
import EmployeeSalaries from './components/EmployeeSalaries';
import EmployeePaymentList from './components/EmployeePaymentList';
import EmployeeList from './components/EmployeeList';
import NassitSchedule from './components/NassitSchedule';
import MedicalContractList from './components/MedicalContractList';
import MedicalAssessmentsDashboard from './components/MedicalAssessmentsDashboard';
import MedicalReportsDashboard from './components/MedicalReportsDashboard';
import AdminDashboard from './components/AdminDashboard';
import TestDocumentAI from './components/testing/TestDocumentAI';
import PatientsDashboard from './components/Patients/PatientsDashboard';
import PatientList from './components/Patients/PatientList';
import PatientVisitList from './components/Patients/PatientVisitList';
import PatientAdmissionList from './components/Patients/PatientAdmissionList';
import LabTestDashboard from './components/Lab/LabTestDashboard';
import LabTestList from './components/Lab/LabTestList';
import LabTestRequestForm from './components/Lab/LabTestRequestForm';
import LabTestRequestList from './components/Lab/LabTestRequestList';
import LabTestRequestDetailForm from './components/Lab/LabTestRequestDetailForm';
import PatientCard from './components/Patients/PatientCard';
import LabTestReport from './components/Lab/LabTestReport';
import PatientBillingList from './components/Patients/PatientBillingList';
import PatientBillingForm from './components/Patients/PatientBillingForm';
import ProcedureList from './components/Procedures/ProcedureList';
import ProcedureForm from './components/Procedures/ProcedureForm';
import ProcedureView from './components/Procedures/ProcedureView';
import MedicationCatalog from './components/Medications/MedicationCatalog';
import AdmissionCard from './components/Admissions/AdmissionCard';
import DoctorLogs from './components/Admissions/DoctorLogs';
import MedicationRequests from './components/Admissions/MedicationRequests';
import VitalsLogs from './components/Admissions/VitalsLogs';
import TreatmentPlan from './components/Admissions/TreatmentPlan';
import ReportList from './components/Reports/ReportList';
import ReportsDashboard from './components/Reports/ReportsDashboard';
import ReportTemplate from './components/Reports/ReportTemplate';
import ReportTemplateRedirect from './components/Reports/ReportTemplateRedirect';
import ReportDesigner from './components/Reports/ReportDesigner';
import ApprovalCodeList from './components/Employee/ApprovalCodeList';
import EmployeeDashboard from './components/Employee/EmployeeDashboard';
import LeaveRequests from './components/LeaveRequests';
import EmployeeCard from './components/Employee/EmployeeCard';
import LeaveBalances from './components/LeaveBalances';
import EmployeeBenefitsDashboard from './components/EmployeeBenefitsDashboard';
import LeavePolicyPage from './components/LeavePolicyPage';
import AdmissionMedicationChart from './components/Admissions/AdmissionMedicationChart';
import AdmissionsDashboard from './components/Admissions/AdmissionsDashboard';
import FHIRQuestionnaire from './components/Admissions/Questionaire/FHIRQuestionnaire';
import JobMonitor from './components/JobMonitor';
import AdminMessaging from './components/AdminMessaging';
import ToastNotifications from './components/ToastNotifications';
import MessageToast from './components/Messages/MessageToast';
import NotificationBanner from './components/NotificationBanner';
import VersionChecker from './components/VersionChecker';
import SiteConfig from './components/SiteConfig';
import NetworkAccessConfig from './components/NetworkAccessConfig';
import AiAdmin from './components/Admin/AiAdmin';
import AuditTrailReport from './components/Admin/AuditTrailReport';
import ApiKeyManagement from './components/ApiKeyManagement';
import DoctorList from './components/Doctor/DoctorList';
import DoctorLandingPage from './components/Doctor/DoctorLandingPage';
import RoleLandingPage from './components/Doctor/RoleLandingPage';
import CashRegisterPage from './components/CashRegister/CashRegisterPage';
import MessagesPage from './components/Messages/MessagesPage';
import MessagesModal from './components/Messages/MessagesModal';
import './styles/AppLayout.css';

function App() {
  return (
    <AuthProvider>
      <SocketProvider>
        <MessagesUIProvider>
          <VersionChecker />
          <Router>
            <div className="app-layout">
              <NotificationBanner />
              <Header />
              <main className="app-layout__main">
                <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<PrivateRoute />}>
            <Route path="/" element={<RoleLandingPage />} />
            <Route path="/home" element={<LandingPage />} />
            <Route path="/account" element={<Account />} />
            <Route path="/doctor-dashboard" element={<DoctorLandingPage />} />
          </Route>
          <Route path="/medical-reports" element={<PrivateRoute />}>
            <Route path="/medical-reports" element={<MedicalReportsList />} />
          </Route>
          <Route path="/sales-form" element={<PrivateRoute />}>
            <Route path="/sales-form" element={<SalesForm />} />
          </Route>
          <Route path="/sales-queue" element={<PrivateRoute />}>
            <Route path="/sales-queue" element={<SalesQueue />} />
          </Route>
          <Route path="/cash-register" element={<PrivateRoute />}>
            <Route path="/cash-register" element={<CashRegisterPage />} />
          </Route>
          <Route path="/user-management" element={<PrivateRoute requiredRoles={['Admin']} requiredPermissions={['View']} />}>
            <Route path="/user-management" element={<UserManagement />} />
          </Route>
          <Route path="/admin-dashboard" element={<PrivateRoute requiredRoles={['Admin','IT']} requiredPermissions={['View']} />}>
            <Route path="/admin-dashboard" element={<AdminDashboard />} />
          </Route>
          <Route path="/role-management" element={<PrivateRoute requiredRoles={['Admin']} requiredPermissions={['View']} />}>
            <Route path="/role-management" element={<RolePermissions />} />
          </Route>
          <Route path="/mfa-settings" element={<PrivateRoute requiredRoles={['Admin']} requiredPermissions={['View']} />}>
            <Route path="/mfa-settings" element={<MfaSettings />} />
          </Route>
          <Route path="/admin-landing" element={<PrivateRoute requiredRoles={['Admin']} requiredPermissions={['View']} />}>
            <Route path="/admin-landing" element={<AdminLanding />} />
          </Route>
          <Route path="/job-monitor" element={<PrivateRoute requiredRoles={['Admin']} requiredPermissions={['View']} />}>
            <Route path="/job-monitor" element={<JobMonitor />} />
          </Route>
          <Route path="/sales-transactions" element={<PrivateRoute />}>
            <Route path="/sales-transactions" element={<SalesTransactions />} />
          </Route>
          <Route path="/customer-invoice" element={<PrivateRoute />}>
            <Route path="/customer-invoice" element={<BulkInvoice />} />
          </Route>
          <Route path="/doctor-sales-summary" element={<PrivateRoute />}>
            <Route path="/doctor-sales-summary" element={<DoctorSalesSummary/>} />
          </Route>
          <Route path="/doctor-sales-details" element={<PrivateRoute />}>
            <Route path="/doctor-sales-details" element={<DoctorSalesDetails/>} />
          </Route>
          <Route path="/doctor-commission-config" element={<PrivateRoute />}>
            <Route path="/doctor-commission-config" element={<DoctorCommissionConfig/>} />
          </Route>
          <Route path="/customer-aging-summary" element={<PrivateRoute />}>
            <Route path="/customer-aging-summary" element={<CustomerAgingSummary/>} />
          </Route>
          <Route path="/employees-dashboard" element={<PrivateRoute />}>
            <Route path="/employees-dashboard" element={<EmployeeDashboard/>} />
          </Route>
          <Route path="/leave-requests" element={<PrivateRoute />}>
            <Route path="/leave-requests" element={<LeaveRequests/>} />
          </Route>
          <Route path="/leave-balances" element={<PrivateRoute />}>
            <Route path="/leave-balances" element={<LeaveBalances/>} />
          </Route>
          <Route path="/employee-benefits" element={<PrivateRoute />}>
            <Route path="/employee-benefits" element={<EmployeeBenefitsDashboard/>} />
          </Route>
          <Route path="/leave-policy" element={<PrivateRoute />}>
            <Route path="/leave-policy" element={<LeavePolicyPage/>} />
          </Route>
          <Route path="/employee-salaries" element={<PrivateRoute />}>
            <Route path="/employee-salaries" element={<EmployeeSalaries/>} />
          </Route>
          <Route path="/employee-payment-list" element={<PrivateRoute />}>
            <Route path="/employee-payment-list" element={<EmployeePaymentList/>} />
          </Route>
          <Route path="/employee-list" element={<PrivateRoute />}>
            <Route path="/employee-list" element={<EmployeeList/>} />
          </Route>
          <Route path="/employee-card" element={<PrivateRoute />}>
            <Route path="/employee-card" element={<EmployeeCard/>} />
          </Route>
          <Route path="/nassit-schedule" element={<PrivateRoute />}>
            <Route path="/nassit-schedule" element={<NassitSchedule/>} />
          </Route>
          <Route path="/doctor-management" element={<PrivateRoute />}>
            <Route path="/doctor-management" element={<DoctorList/>} />
          </Route>
          <Route path="/medical-contract-list" element={<PrivateRoute />}>
            <Route path="/medical-contract-list" element={<MedicalContractList/>} />
          </Route>
          <Route path="/medical-assessments-dashboard" element={<PrivateRoute />}>
            <Route path="/medical-assessments-dashboard" element={<MedicalAssessmentsDashboard/>} />
          </Route>
          <Route path="/medical-reports-dashboard" element={<PrivateRoute />}>
            <Route path="/medical-reports-dashboard" element={<MedicalReportsDashboard/>} />
          </Route>
          <Route path="/patients-dashboard" element={<PrivateRoute />}>
            <Route path="/patients-dashboard" element={<PatientsDashboard/>} />
          </Route>
          <Route path="/patients" element={<PrivateRoute />}>
            <Route path="/patients" element={<PatientList/>} />
          </Route>
          <Route path="/patient-visits" element={<PrivateRoute />}>
            <Route path="/patient-visits" element={<PatientVisitList/>} />
          </Route>
          <Route path="/patient-admissions" element={<PrivateRoute />}>
            <Route path="/patient-admissions" element={<PatientAdmissionList/>} />
          </Route>
          <Route path="/admissions-dashboard" element={<PrivateRoute />}>
            <Route path="/admissions-dashboard" element={<AdmissionsDashboard/>} />
          </Route>
          <Route path="/medication-catalog" element={<PrivateRoute />}>
            <Route path="/medication-catalog" element={<MedicationCatalog />} />
          </Route>
          <Route path="/labTests" element={<PrivateRoute />}>
            <Route path="/labTests" element={<LabTestList/>} />
          </Route>
          <Route path="/lab" element={<PrivateRoute />}>
            <Route path="/lab" element={<LabTestDashboard/>} />
          </Route>
          <Route path="/lab-request-form" element={<PrivateRoute />}>
            <Route path="/lab-request-form" element={<LabTestRequestForm/>} />
          </Route>
          <Route path="/lab-requests" element={<PrivateRoute />}>
            <Route path="/lab-requests" element={<LabTestRequestList viewName={'Laboratory'}/>} />
          </Route>
          <Route path="/radiology-requests" element={<PrivateRoute />}>
            <Route path="/radiology-requests" element={<LabTestRequestList viewName={'Radiology'}/>} />
          </Route>
          <Route path="/lab-request-details" element={<PrivateRoute />}>
            <Route path="/lab-request-details" element={<LabTestRequestDetailForm/>} />
          </Route>
          <Route path="/patient-card" element={<PrivateRoute />}>
            <Route path="/patient-card" element={<PatientCard/>} />
          </Route>
          <Route path="/lab-report" element={<PrivateRoute />}>
            <Route path="/lab-report" element={<LabTestReport/>} />
          </Route>
          <Route path="/patient-billing" element={<PrivateRoute />}>
            <Route path="/patient-billing" element={<PatientBillingList/>} />
          </Route>
          <Route path="/patient-billing-form" element={<PrivateRoute />}>
            <Route path="/patient-billing-form" element={<PatientBillingForm/>} />
          </Route>
          <Route path="/procedures" element={<PrivateRoute />}>
            <Route path="/procedures" element={<ProcedureList/>} />
          </Route>
          <Route path="/procedure-form" element={<PrivateRoute />}>
            <Route path="/procedure-form" element={<ProcedureForm/>} />
          </Route>
          <Route path="/procedure-view" element={<PrivateRoute />}>
            <Route path="/procedure-view" element={<ProcedureView/>} />
          </Route>
          <Route path="/admission-card" element={<PrivateRoute />}>
            <Route path="/admission-card" element={<AdmissionCard />} />
            <Route path="/admission-card/doctor-logs" element={<DoctorLogs />} />
            <Route path="/admission-card/medication-requests" element={<MedicationRequests />} />
            <Route path="/admission-card/vitals-logs" element={<VitalsLogs />} />
            <Route path="/admission-card/treatment-plans" element={<TreatmentPlan />} />
          </Route>
          <Route path="/reports" element={<PrivateRoute />}>
            <Route path="/reports" element={<ReportList />} />
          </Route>
          <Route path="/reports/:reportId/edit" element={<PrivateRoute />}>
            <Route path="/reports/:reportId/edit" element={<ReportDesigner />} />
          </Route>
          <Route path="/reports-dashboard" element={<PrivateRoute />}>
            <Route path="/reports-dashboard" element={<ReportsDashboard />} />
          </Route>
          <Route path="/reports-dashboard/:reportId" element={<PrivateRoute />}>
            <Route path="/reports-dashboard/:reportId" element={<ReportTemplate />} />
          </Route>
          <Route path="/report-template" element={<PrivateRoute />}>
            <Route path="/report-template" element={<ReportTemplateRedirect />} />
          </Route>
          <Route path="/employee-access-codes" element={<PrivateRoute />}>
            <Route path="/employee-access-codes" element={<ApprovalCodeList />} />
          </Route>
          <Route path="/admission-medication-chart" element={<PrivateRoute />}>
            <Route path="/admission-medication-chart" element={<AdmissionMedicationChart />} />
          </Route>
          <Route path="/admission-patient-history" element={<PrivateRoute />}>
            <Route path="/admission-patient-history" element={<FHIRQuestionnaire />} />
          </Route>
          <Route path="/messages" element={<PrivateRoute />}>
            <Route path="/messages" element={<MessagesPage />} />
          </Route>
          <Route path="/admin/messaging" element={<PrivateRoute requiredRoles={['Admin']} requiredPermissions={['View']} />}>
            <Route path="/admin/messaging" element={<AdminMessaging />} />
          </Route>
          <Route path="/site-config" element={<PrivateRoute requiredRoles={['Admin']} requiredPermissions={['View']} />}>
            <Route path="/site-config" element={<SiteConfig />} />
          </Route>
          <Route path="/network-access-config" element={<PrivateRoute />}>
            <Route path="/network-access-config" element={<NetworkAccessConfig />} />
          </Route>
          <Route path="/ai-admin" element={<PrivateRoute  />}>
            <Route path="/ai-admin" element={<AiAdmin />} />
          </Route>
          <Route path="/api-key-management" element={<PrivateRoute requiredRoles={['Admin','IT']} requiredPermissions={['View']} />}>
            <Route path="/api-key-management" element={<ApiKeyManagement />} />
          </Route>
          <Route path="/audit-trail" element={<PrivateRoute requiredRoles={['Admin']} requiredPermissions={['View']} />}>
            <Route path="/audit-trail" element={<AuditTrailReport />} />
          </Route>
          <Route path="/medical-exam-export" element={<PrivateRoute />}>
            <Route path="/medical-exam-export" element={<MedicalExamExport />} />
          </Route>
          <Route path="/testing/test-document-ai" element={<TestDocumentAI />} />
            </Routes>
              </main>
              <ToastNotifications />
              <MessageToast />
              <MessagesModal />
            </div>
          </Router>
        </MessagesUIProvider>
      </SocketProvider>
    </AuthProvider>
  );
}

export default App;
